import classNames from 'classnames';
import React, {
  cloneElement, useState, useEffect, useRef
} from 'react';
import PropTypes from 'prop-types';
import { Icon } from '../KeyComponents';
import InlineDropdownItem from './InlineDropdownItem';

function InlineDropdown(props) {
  const {
    children,
    className,
    onSelect,
    disabled,
    readonly,
    selected,
    bodyClassName,
    placeholder,
    border,
    icon,
    iconType,
    prefix
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const dropdownRef = useRef(null);

  useEffect(() => {
    // Set the initial selected item based on the `selected` prop
    if (selected) {
      const initialSelectedItem = React.Children.toArray(children)
        .find(child => child.type === InlineDropdownItem && child.props.value === selected);

      if (initialSelectedItem) {
        setSelectedItem(cloneElement(initialSelectedItem, {
          className: classNames('inlinedropdown-item', 'inlinedropdown-item-selected'),
        }));
      }
    }

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [children, selected]);

  const DropdownItemComp = React.Children.map(children, (child) => {
    if (child.type === InlineDropdownItem) {
      return cloneElement(child, {
        className: classNames('inlinedropdown-item', {
          'inlinedropdown-item-selected': selectedItem && child.props.label === selectedItem.props.label,
        }),
        onClick: () => {
          setSelectedItem(cloneElement(child, {
            className: classNames('inlinedropdown-item', 'inlinedropdown-item-selected'),
          }));
          setIsOpen(false);
          if (onSelect) {
            onSelect(child.props);
          }
          child.props.onClick();
        }
      });
    }
    return null;
  });

  const toggleDropdown = (e) => {
    if (disabled || readonly) { return; }
    if (e.type === 'click' || e.key === 'Enter' || e.key === ' ') {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div ref={dropdownRef} className={classNames('png__inlinedropdown', { 'inlinedropdown-disabled': disabled }, className)}>
      <div
        className={classNames('inlinedropdown-body', bodyClassName, { 'inlinedropdown-toggle': isOpen }, { 'inlinedropdown-border': border, 'inlinedropdown-readonly': readonly })}
        onClick={toggleDropdown}
        onKeyDown={toggleDropdown}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
      >
        {icon && <Icon size="xs" icon={icon} type={iconType} />}
        {
          (selectedItem && React.cloneElement(selectedItem, { label: `${prefix}${selectedItem.props.label}` })) || placeholder
        }
        {!readonly && (
        <Icon
          className="inlinedropdown-input-icon"
          icon="arrow-down-s"
          type="line"
        />
        )}
      </div>

      <div className="inlinedropdown-drawer">
        {DropdownItemComp}
      </div>
    </div>
  );
}

InlineDropdown.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  selected: PropTypes.string,
  bodyClassName: PropTypes.string,
  border: PropTypes.bool,
  placeholder: PropTypes.string,
  prefix: PropTypes.string,
  icon: PropTypes.string,
  iconType: PropTypes.string
};

InlineDropdown.defaultProps = {
  children: null,
  className: '',
  onSelect: () => { },
  disabled: false,
  selected: '',
  readonly: false,
  bodyClassName: '',
  border: false,
  placeholder: 'Select',
  prefix: '',
  icon: '',
  iconType: ''
};

InlineDropdown.Item = InlineDropdownItem;

export default InlineDropdown;
